import {User} from '@hconnect/apiclient'
import {QueryObserverResult} from '@tanstack/react-query'
import React, {useEffect, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'

import {useLoggedInUserPermissions} from '../../hooks/useLoggedInUser'
import {useRolesDataScopeAccess} from '../../hooks/useRolesDataScopeAccess'
import {useTranslation} from '../../hooks/useTranslation'
import {prettyRoleName} from '../../layouts/Roles/utils'
import {getDataScopeAccessError, hasDataScopeAccessCheck} from '../../modules/DataScopeAccess'
import {getDataScopeWeight} from '../../modules/DataScopeConfiguration'
import {Permission} from '../../modules/LoggedInUser'
import {
  cleanGroupRoleAssignments,
  GroupedRoleAssignment,
  groupRoleAssignments,
  RoleAssignment
} from '../../modules/ManageUsers.selectors'

import {RolesView} from './RolesView'

type RolesViewContainerProps = {
  roleAssignments?: RoleAssignment[] | null
  newRoleAssignments?: GroupedRoleAssignment[]
  showAutoGeneratedRoles: boolean
  refetchRoles: () => Promise<QueryObserverResult<RoleAssignment[] | null>>
  showNewRoleAssignmentForm: boolean
  setShowNewRoleAssignmentForm: (show: boolean) => void
  isAllCollapsed: boolean
  loggedInUserPermissions?: Permission[] | null
  loggedInUserProfile?: User | null
  isUserCreation?: boolean
}

export const RolesViewContainer = ({
  roleAssignments,
  showAutoGeneratedRoles,
  loggedInUserPermissions,
  loggedInUserProfile,
  refetchRoles,
  showNewRoleAssignmentForm,
  setShowNewRoleAssignmentForm,
  isAllCollapsed,
  isUserCreation,
  newRoleAssignments
}: RolesViewContainerProps) => {
  const loggedInUserPermissionsData = useLoggedInUserPermissions(loggedInUserPermissions)
  const {userId} = useParams<{userId?: string}>()
  const containerRef = useRef<HTMLDivElement>(null)
  const {t} = useTranslation()

  const [newlyCreatedRoleIds, setNewlyCreatedRoleIds] = useState<Set<number>>(new Set())

  const [previousRoleCount, setPreviousRoleCount] = useState<number>(() => {
    return roleAssignments?.length || 0
  })

  const sortDataScopes = (dataScopeA: string, dataScopeB: string) =>
    getDataScopeWeight(dataScopeA) - getDataScopeWeight(dataScopeB)

  const filteredRoleAssignments = showAutoGeneratedRoles
    ? roleAssignments
    : roleAssignments?.filter((role) => !role.autoGenerated)

  const groupedRoleAssignments = isUserCreation
    ? newRoleAssignments
    : filteredRoleAssignments &&
      groupRoleAssignments(filteredRoleAssignments).map(cleanGroupRoleAssignments)

  const roleDataScopeAccessQueryInfo = useRolesDataScopeAccess(groupedRoleAssignments ?? [])

  const sortRoles = (a: GroupedRoleAssignment, b: GroupedRoleAssignment) => {
    const formattedRoleTypeA = t(
      `roleAssignment.roleTypes.${a.roleType}`,
      prettyRoleName(a.roleType)
    )

    const formattedRoleTypeB = t(
      `roleAssignment.roleTypes.${b.roleType}`,
      prettyRoleName(b.roleType)
    )
    if (formattedRoleTypeA === null && formattedRoleTypeB === null) {
      return 0
    } else if (formattedRoleTypeA === null) {
      return -1
    } else if (formattedRoleTypeB === null) {
      return 1
    } else {
      return formattedRoleTypeA.localeCompare(formattedRoleTypeB)
    }
  }

  const sortedRoleAssignments: GroupedRoleAssignment[] =
    Array.isArray(groupedRoleAssignments) &&
    (isUserCreation ? newRoleAssignments : roleAssignments) &&
    groupedRoleAssignments
      ? groupedRoleAssignments.sort((a: GroupedRoleAssignment, b: GroupedRoleAssignment) => {
          const aIsNew = a.sourceRolesIds.some((id) => newlyCreatedRoleIds.has(id))
          const bIsNew = b.sourceRolesIds.some((id) => newlyCreatedRoleIds.has(id))

          if (aIsNew === bIsNew) {
            return sortRoles(a, b)
          }

          return aIsNew ? -1 : 1
        })
      : []

  const commonDataScopes = (groupedRoleAssignment: GroupedRoleAssignment) => {
    return Object.keys(groupedRoleAssignment.commonDataScopes).sort(sortDataScopes)
  }

  const unsupportedDataScopes = (groupedRoleAssignment: GroupedRoleAssignment) =>
    Object.keys(groupedRoleAssignment.unsupportedDataScopes).sort(sortDataScopes)

  const isInternal = (groupedRoleAssignment: GroupedRoleAssignment) =>
    roleAssignments?.find((item) => item.roleType === groupedRoleAssignment.roleType)?.isInternal ??
    false

  const getAccessError = (groupedRoleAssignment: GroupedRoleAssignment): string | null => {
    if (roleDataScopeAccessQueryInfo.isLoading || roleDataScopeAccessQueryInfo.isError) {
      return hasDataScopeAccessCheck(groupedRoleAssignment.commonDataScopes)
        ? 'roleAssignment.dataScopeAccessUnavailable'
        : null
    }

    return roleDataScopeAccessQueryInfo.data
      ? getDataScopeAccessError(
          groupedRoleAssignment.commonDataScopes,
          roleDataScopeAccessQueryInfo.data
        )
      : null
  }

  const newGroupedRoleAssignment: GroupedRoleAssignment = {
    groupId: '',
    sourceRolesIds: [],
    roleType: '',
    userId: userId || '',
    commonDataScopes: {},
    variantDataScopes: {
      customersScopes: [],
      allCustomersRoleId: null
    },
    unsupportedDataScopes: {}
  }

  useEffect(() => {
    if (showNewRoleAssignmentForm && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }, [showNewRoleAssignmentForm])

  useEffect(() => {
    if (roleAssignments && roleAssignments.length > 0) {
      const currentRoleCount = roleAssignments.length

      if (previousRoleCount > 0 && currentRoleCount > previousRoleCount) {
        const maxId = Math.max(...roleAssignments.map((role) => role.id))

        setNewlyCreatedRoleIds((prev) => new Set([...prev, maxId]))
      }

      setPreviousRoleCount(currentRoleCount)
    }
  }, [roleAssignments, previousRoleCount])

  return (
    <div className="flex flex-col gap-3" ref={containerRef}>
      {showNewRoleAssignmentForm && (
        <RolesView
          key={newGroupedRoleAssignment.groupId || 'newGroup'}
          commonDataScopes={commonDataScopes(newGroupedRoleAssignment)}
          groupedRoleAssignment={newGroupedRoleAssignment}
          loggedInUserPermissions={loggedInUserPermissionsData}
          loggedInUserProfile={loggedInUserProfile}
          isInternal={isInternal(newGroupedRoleAssignment)}
          roleAssignments={roleAssignments}
          refetchRoles={refetchRoles}
          isNewRole={true}
          setShowNewRoleAssignmentForm={setShowNewRoleAssignmentForm}
          isAllCollapsed={false}
          isUserCreation={isUserCreation}
          dataScopeAccessError={null}
        />
      )}
      {sortedRoleAssignments.map((groupedRoleAssignment) => (
        <RolesView
          key={groupedRoleAssignment.groupId || 'newGroup'}
          commonDataScopes={commonDataScopes(groupedRoleAssignment)}
          groupedRoleAssignment={groupedRoleAssignment}
          loggedInUserPermissions={loggedInUserPermissionsData}
          loggedInUserProfile={loggedInUserProfile}
          isInternal={isInternal(groupedRoleAssignment)}
          roleAssignments={roleAssignments}
          refetchRoles={refetchRoles}
          setShowNewRoleAssignmentForm={setShowNewRoleAssignmentForm}
          isAllCollapsed={isAllCollapsed}
          isUserCreation={isUserCreation}
          unsupportedDataScopes={unsupportedDataScopes(groupedRoleAssignment)}
          dataScopeAccessError={getAccessError(groupedRoleAssignment)}
        />
      ))}
    </div>
  )
}
